import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';

import { AuthProvider } from '@context/AuthContext';
import { ErrorBoundary } from '@components/ui/ErrorBoundary';
import { Button } from '@components/ui/Button';
import App from './App';
import './index.css';

/** La caída de toda la app: pantalla completa con «Recargar» (era el fallback de `components/ErrorBoundary.tsx`, apagado en I-229). */
const PANTALLA_ROTA = (
  <div style={{ padding: 40, textAlign: 'center' }}>
    <h2>Algo salió mal</h2>
    <p>El error fue reportado. Recarga la página o vuelve a intentar.</p>
    <Button variant="primary" onClick={() => window.location.reload()}>Recargar</Button>
  </div>
);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutos
    },
  },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <ErrorBoundary fallback={PANTALLA_ROTA}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
            <Toaster
              position="top-right"
              gutter={8}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#0F172A',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  maxWidth: '380px',
                },
                success: {
                  iconTheme: { primary: '#10B981', secondary: '#fff' },
                },
                error: {
                  iconTheme: { primary: '#EF4444', secondary: '#fff' },
                },
              }}
            />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
